<template>
  <SGPContainerComponent>
    <SGPLoadingComponent v-if="loadingUsuario" />

    <template v-else>
      <!-- Cabeçalho com info do usuário -->
      <v-card class="mb-4" outlined>
        <v-card-text class="d-flex align-center py-3">
          <v-avatar color="primary" size="48" class="mr-4">
            <span class="white--text text-h6">{{ iniciais }}</span>
          </v-avatar>
          <div>
            <div class="text-h6 font-weight-medium">{{ usuario.name }}</div>
            <div class="grey--text text-body-2">{{ usuario.email }}</div>
          </div>
          <v-spacer />
          <v-btn text @click="$router.push({ name: 'usuarios' })">
            <v-icon left>mdi-arrow-left</v-icon>
            Voltar
          </v-btn>
        </v-card-text>
      </v-card>

      <!-- Associar módulo -->
      <v-card class="mb-4">
        <v-card-title class="text-subtitle-1 font-weight-bold">
          <v-icon left color="primary">mdi-folder-plus-outline</v-icon>
          Associar a módulo
        </v-card-title>
        <v-card-text>
          <v-row dense align="end">
            <v-col cols="12" md="5">
              <SGPFormAutocompleteComponent
                v-model="novaAssociacao.moduloId"
                :listaDeOptions="modulosDisponiveis"
                :optionLabel="'nome'"
                :optionValue="'id'"
                placeholder="Módulo"
              />
            </v-col>
            <v-col cols="12" md="5">
              <SGPFormAutocompleteComponent
                v-model="novaAssociacao.role_id"
                :listaDeOptions="listaDePerfis"
                :optionLabel="'name'"
                :optionValue="'id'"
                placeholder="Perfil"
              />
            </v-col>
            <v-col cols="12" md="2">
              <v-btn
                color="primary"
                block
                :loading="salvando"
                :disabled="!novaAssociacao.moduloId || !novaAssociacao.role_id"
                @click="associar"
              >
                <v-icon left>mdi-plus</v-icon>
                Associar
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Módulos associados -->
      <v-card>
        <v-card-title class="text-subtitle-1 font-weight-bold">
          <v-icon left color="primary">mdi-folder-account-outline</v-icon>
          Módulos associados
          <v-chip small class="ml-2" color="primary" outlined>{{ modulosAssociados.length }}</v-chip>
        </v-card-title>

        <v-card-text v-if="!modulosAssociados.length" class="text-center grey--text py-8">
          <v-icon large color="grey lighten-1">mdi-folder-open-outline</v-icon>
          <div class="mt-2">Nenhum módulo associado a este usuário.</div>
        </v-card-text>

        <v-simple-table v-else>
          <template #default>
            <thead>
              <tr>
                <th>Módulo</th>
                <th>Perfil</th>
                <th class="text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="mod in modulosAssociados" :key="mod.id">
                <td>
                  <div class="d-flex align-center">
                    <v-icon v-if="mod.icon" small class="mr-2 grey--text">{{ mod.icon }}</v-icon>
                    <span>{{ mod.nome }}</span>
                  </div>
                </td>
                <td style="min-width: 220px; padding-top: 4px; padding-bottom: 4px">
                  <SGPFormAutocompleteComponent
                    v-model="mod.pivot_role_id"
                    :listaDeOptions="listaDePerfis"
                    :optionLabel="'name'"
                    :optionValue="'id'"
                    placeholder="Perfil"
                    :somenteLeitura="!modulosGerenciaveis.has(mod.id)"
                  />
                </td>
                <td class="text-right" style="white-space: nowrap">
                  <v-tooltip bottom>
                    <template #activator="{ on }">
                      <v-btn icon small color="primary" v-on="on"
                        :disabled="!modulosGerenciaveis.has(mod.id)"
                        @click="atualizarRole(mod)">
                        <v-icon small>mdi-content-save</v-icon>
                      </v-btn>
                    </template>
                    <span>Salvar perfil</span>
                  </v-tooltip>
                  <v-tooltip bottom>
                    <template #activator="{ on }">
                      <v-btn icon small color="error" v-on="on"
                        :disabled="!modulosGerenciaveis.has(mod.id)"
                        @click="desassociar(mod)">
                        <v-icon small>mdi-account-remove</v-icon>
                      </v-btn>
                    </template>
                    <span>Remover acesso</span>
                  </v-tooltip>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-card>
    </template>
  </SGPContainerComponent>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'UsuarioModuloAssociacaoPage',

  data() {
    return {
      salvando: false,
      novaAssociacao: {
        moduloId: null,
        role_id: null
      }
    }
  },

  computed: {
    ...mapGetters('usuarios', ['usuario', 'loadingUsuario']),
    ...mapGetters('ctrlModulos', ['modulos']),
    ...mapGetters('perfis', ['listaDePerfis']),

    iniciais() {
      const nome = this.usuario?.name || ''
      return nome
        .split(' ')
        .slice(0, 2)
        .map(p => p[0])
        .join('')
        .toUpperCase()
    },

    modulosAssociados() {
      const mods = this.usuario?.modulos || []
      return mods.map(m => ({ ...m, pivot_role_id: m.pivot?.role_id || null }))
    },

    modulosDisponiveis() {
      const associadosIds = this.modulosAssociados.map(m => m.id)
      return this.modulos
        .filter(m => m.has_access && m.role_name === 'administrador')
        .filter(m => !associadosIds.includes(m.id))
    },

    modulosGerenciaveis() {
      return new Set(
        this.modulos
          .filter(m => m.has_access && m.role_name === 'administrador')
          .map(m => m.id)
      )
    }
  },

  async created() {
    await Promise.all([
      this.getUsuario(this.$route.params.usuario),
      this.buscarModulos(),
      this.listarPerfis()
    ])
  },

  methods: {
    ...mapActions('usuarios', ['getUsuario']),
    ...mapActions('ctrlModulos', ['buscarModulos', 'associarUsuarioAModulo', 'atualizarRoleUsuario', 'desassociarUsuario']),
    ...mapActions('perfis', ['listarPerfis']),

    async associar() {
      this.salvando = true
      try {
        const resp = await this.associarUsuarioAModulo({
          moduloId: this.novaAssociacao.moduloId,
          user_id: this.usuario.id,
          role_id: this.novaAssociacao.role_id
        })
        if (!resp) return
        await this.getUsuario(this.$route.params.usuario)
        this.novaAssociacao = { moduloId: null, role_id: null }
      } finally {
        this.salvando = false
      }
    },

    async atualizarRole(mod) {
      await this.atualizarRoleUsuario({
        moduloId: mod.id,
        userId: this.usuario.id,
        role_id: mod.pivot_role_id
      })
    },

    async desassociar(mod) {
      await this.desassociarUsuario({
        moduloId: mod.id,
        userId: this.usuario.id
      })
      await this.getUsuario(this.$route.params.usuario)
    }
  }
}
</script>
